/**
 * Chat Input Form Component
 *
 * Provides input field, file upload, and send button for user messages.
 * Supports file uploads: images, PDF (max 5/10 MB) and data files for KIAN imports
 * (JSON, CSV, Excel, Text, E-Mail — max 10 MB), 3 files per message
 */

import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { Group, Textarea, Stack, Text, Alert, Box, ActionIcon, Tooltip, Loader, Progress, Button } from '@mantine/core';
import { IconSend, IconAlertCircle, IconLoader, IconPaperclip, IconX, IconFile, IconScreenShare, IconPlayerStop, IconMicrophone, IconMicrophoneOff, IconBolt, IconHeadset } from '@tabler/icons-react';
import { useScreenshotCapture } from '../../hooks/useScreenshotCapture';
import { useClipboardPaste } from '../../hooks/useClipboardPaste';
import { useKianFlyoutStore } from '../../stores/kianFlyout';
import { getAllSlashForType } from './flyout/kianPromptCatalog';
import { KianSlashMenu } from './flyout/KianSlashMenu';
import { useVoiceInput } from '../../hooks/useVoiceInput';
import { attachmentStatusLabel } from './attachmentStatusLabel';
import { api } from '../../lib/api';

interface FilePreview {
  file: File;
  preview?: string;
  error?: string;
}

interface ChatInputFormProps {
  onSendMessage: (content: string, files?: File[], hints?: Record<string, unknown>) => Promise<void>;
  onInputChange?: (content: string) => void;
  disabled?: boolean;
  isLoading?: boolean;
  error?: string | null;
  /** Flyout: Entwurf pro Seitenkontext persistieren */
  draftContextKey?: string;
  /** Flyout: Slash-Befehle (/summary …) passend zum Entity-Typ */
  entityTypeForSlash?: string | null;
  /** Aktiven Stream abbrechen */
  onAbortStream?: () => void;
  /** Anhänge sind unterwegs (Bildanalyse, PDF-Text, Dateien lesen) und KIAN antwortet */
  isAnalyzingFiles?: boolean;
  /** Upload-Fortschritt (0–100) oder null wenn kein Upload aktiv */
  uploadProgress?: number | null;
  /** Read-only Modus für archivierte Chats — versteckt Composer, zeigt Banner */
  readOnly?: boolean;
  /** Handler für "Wiederherstellen"-Button im Read-only-Banner */
  onRestore?: () => Promise<void> | void;
  /** KIAN-Flyout: rechter Innenabstand, damit der fixe FAB die Action-Buttons nicht überlappt */
  reserveFabAffixSpace?: boolean;
  /** Gesprächsmodus starten (sprechen und zuhören) — ohne Handler kein Knopf */
  onStartVoiceConversation?: () => void;
  /** Gesprächsmodus läuft — Diktat und Start-Knopf ruhen */
  voiceConversationActive?: boolean;
}

const SUPPORTED_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/heic',
  'image/heif',
  'application/pdf',
];
const SUPPORTED_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.heic', '.heif', '.pdf'];
/**
 * Datendateien für KIAN (z. B. Tickets aus einer Liste anlegen). Muss mit
 * backend/src/middleware/fileValidation.ts KIAN_DATA_EXTENSIONS übereinstimmen.
 */
const DATA_EXTENSIONS = ['.json', '.jsonl', '.ndjson', '.csv', '.tsv', '.txt', '.md', '.markdown', '.xlsx', '.html', '.htm', '.eml'];
// Muss mit backend/src/middleware/fileValidation.ts MAX_SCREENSHOT_SIZE_BYTES übereinstimmen.
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
/** Listen und PDFs dürfen größer sein (Backend: KIAN_DATA_FILE_VALIDATION.MAX_SIZE_BYTES). */
const MAX_DATA_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const MAX_FILES = 3;
/** Synchron mit Minimize-FAB in `KianFAB.tsx` (panelOpen, size={40}) */
const KIAN_FAB_MINIMIZE_SIZE_PX = 40;

export function ChatInputForm({
  onSendMessage,
  onInputChange,
  disabled,
  isLoading,
  error,
  draftContextKey,
  entityTypeForSlash,
  onAbortStream,
  isAnalyzingFiles,
  uploadProgress,
  readOnly = false,
  onRestore,
  reserveFabAffixSpace = false,
  onStartVoiceConversation,
  voiceConversationActive = false,
}: ChatInputFormProps) {
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [forceDeep, setForceDeep] = useState(false);

  const {
    supported: voiceSupported,
    status: voiceStatus,
    error: voiceError,
    serverVoice,
    startListening,
    stopListening,
  } = useVoiceInput((transcript) => setMessage((prev) => (prev ? `${prev} ${transcript}` : transcript)));
  const [files, setFiles] = useState<FilePreview[]>([]);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isExtractingText, setIsExtractingText] = useState(false);
  const [slashOpen, setSlashOpen] = useState(false);
  const [pendingHints, setPendingHints] = useState<Record<string, unknown> | undefined>();
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  /** Jüngste Fassung von validateAndAddFile — der Effekt oben läuft vor deren Definition. */
  const validateAndAddFileRef = useRef<(file: File) => void>(() => {});

  const setDraftForContext = useKianFlyoutStore((s) => s.setDraftForContext);
  const inputInjection = useKianFlyoutStore((s) => s.inputInjection);
  const skipNextDraftPersist = useRef(false);

  const slashDefs = useMemo(() => getAllSlashForType(entityTypeForSlash ?? null), [entityTypeForSlash]);
  const slashFiltered = useMemo(() => {
    if (!message.startsWith('/')) return slashDefs;
    const needle = message.slice(1).toLowerCase();
    return slashDefs.filter(
      (d) =>
        !needle ||
        d.cmd.toLowerCase().startsWith(needle) ||
        d.label.toLowerCase().includes(needle)
    );
  }, [message, slashDefs]);

  useEffect(() => {
    if (!draftContextKey) return;
    skipNextDraftPersist.current = true;
    setMessage(useKianFlyoutStore.getState().getDraftForContext(draftContextKey));
  }, [draftContextKey]);

  useEffect(() => {
    if (!inputInjection) return;
    setMessage(inputInjection.text);
    useKianFlyoutStore.getState().clearInputInjection();
    requestAnimationFrame(() => inputRef.current?.focus());
  }, [inputInjection?.id, inputInjection]);

  // Dateien von außen (KIAN-Fenster: „Tickets aus Datei") — wie ein Datei-Drop behandeln
  const fileInjection = useKianFlyoutStore((s) => s.fileInjection);
  useEffect(() => {
    if (!fileInjection) return;
    useKianFlyoutStore.getState().clearFileInjection();
    fileInjection.files.forEach((f) => validateAndAddFileRef.current(f));
    if (fileInjection.text) setMessage((prev) => (prev.trim() ? prev : fileInjection.text!));
    requestAnimationFrame(() => inputRef.current?.focus());
  }, [fileInjection?.id, fileInjection]);

  // Debounced draft saving — prevents a store write on every keystroke
  useEffect(() => {
    if (!draftContextKey) return;
    if (skipNextDraftPersist.current) {
      skipNextDraftPersist.current = false;
      return;
    }
    const timer = setTimeout(() => {
      const existing = useKianFlyoutStore.getState().getDraftForContext(draftContextKey);
      if (existing === message) return;
      setDraftForContext(draftContextKey, message);
    }, 500);
    return () => clearTimeout(timer);
  }, [message, draftContextKey, setDraftForContext]);

  useEffect(() => {
    if (message.startsWith('/')) {
      const rest = message.slice(1);
      if (rest === '' || /^[a-z0-9_-]*$/i.test(rest)) {
        setSlashOpen(true);
      } else {
        setSlashOpen(false);
      }
    } else {
      setSlashOpen(false);
    }
  }, [message]);

  // Screenshot Capture Hook
  const { isCapturing, isSupported: screenshotSupported, captureScreenshot, error: captureError, clearError: clearCaptureError } = useScreenshotCapture();
  // Clipboard Paste Hook
  useClipboardPaste({
    onPaste: (pastedFiles) => {
      pastedFiles.forEach(validateAndAddFile);
    },
    targetRef: inputRef as unknown as React.RefObject<HTMLElement>,
    allowedMimeTypes: [
      'image/png',
      'image/jpeg',
      'image/gif',
      'image/webp',
      'image/heic',
      'image/heif',
    ],
  });

  // Wenn Screenshot-Fehler auftritt, zeige ihn an
  const displayError = uploadError || captureError || (voiceStatus === 'error' ? voiceError : null) || error;

  const validateAndAddFile = (file: File) => {
    setUploadError(null);

    // Check file type (MIME or fallback via extension — manche Systeme liefern kein MIME für HEIC)
    const lowerName = file.name.toLowerCase();
    const extOk = SUPPORTED_EXTENSIONS.some((ext) => lowerName.endsWith(ext));
    const mimeOk = SUPPORTED_TYPES.includes(file.type);
    const isData = DATA_EXTENSIONS.some((ext) => lowerName.endsWith(ext));
    if (!mimeOk && !extOk && !isData) {
      setUploadError('Erlaubte Dateitypen: Bilder (JPG, PNG, GIF, WebP, HEIC), PDF sowie JSON, CSV, Excel (.xlsx), Text und E-Mail (.eml)');
      return;
    }

    // Check file size
    const largeAllowed = isData || lowerName.endsWith('.pdf');
    if (file.size > (largeAllowed ? MAX_DATA_FILE_SIZE : MAX_FILE_SIZE)) {
      setUploadError(largeAllowed ? 'Datei zu groß (max. 10 MB)' : 'Datei zu groß (max. 5 MB)');
      return;
    }

    // Check max files
    if (files.length >= MAX_FILES) {
      setUploadError(`Maximal ${MAX_FILES} Dateien pro Nachricht`);
      return;
    }

    // Create preview for images
    let preview: string | undefined;
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setFiles((prev) =>
          prev.map((f) =>
            f.file === file ? { ...f, preview: e.target?.result as string } : f
          )
        );
      };
      reader.onerror = () => {
        setUploadError('Vorschau konnte nicht geladen werden');
      };
      reader.readAsDataURL(file);
    }

    setFiles((prev) => [...prev, { file, preview }]);

    // Auto-extract text from images for pre-fill (server analysis still runs on send)
    if (file.type.startsWith('image/') || file.type === 'image/heic' || file.type === 'image/heif') {
      setIsExtractingText(true);
      api.ocrPreview(file)
        .then(({ text }) => {
          if (text?.trim()) {
            setMessage((prev) => prev ? `${prev}\n\n${text}` : text);
          }
        })
        .catch((err) => { console.warn('[ChatInput] OCR preview failed:', err); })
        .finally(() => setIsExtractingText(false));
    }
  };

  validateAndAddFileRef.current = validateAndAddFile;

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newFiles = Array.from(e.currentTarget.files || []);
    newFiles.forEach(validateAndAddFile);
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeFile = (file: File) => {
    setFiles((prev) => prev.filter((f) => f.file !== file));
    setUploadError(null);
  };

  const handleSubmit = async () => {
    if ((!message.trim() && files.length === 0) || submitting || disabled) return;

    setSubmitting(true);
    try {
      const h = { ...(pendingHints ?? {}), ...(forceDeep ? { forceDeep: true } : {}) };
      setPendingHints(undefined);
      setForceDeep(false);
      await onSendMessage(
        message.trim(),
        files.length > 0 ? files.map((f) => f.file) : undefined,
        Object.keys(h).length ? h : undefined
      );
      setMessage('');
      setFiles([]);
      inputRef.current?.focus();
    } catch (error) {
      console.error('Failed to send message:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Escape') {
      setSlashOpen(false);
      return;
    }
    // Cmd/Ctrl+Enter: Immer senden
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      void handleSubmit();
      return;
    }
    // Enter ohne Shift (und Slash-Menü zu): Senden
    if (e.key === 'Enter' && !e.shiftKey && !slashOpen) {
      e.preventDefault();
      void handleSubmit();
    }
    // Shift+Enter oder Enter im Slash-Menü: Default (Zeilenumbruch oder Menü-Nav)
  };

  const handleCaptureScreenshot = async () => {
    clearCaptureError();
    const file = await captureScreenshot();
    if (file) {
      validateAndAddFile(file);
    }
  };

  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const onDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!e.dataTransfer?.types?.includes('Files')) return;
    setDragActive(true);
  };

  const onDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Only deactivate when the cursor leaves the drop zone entirely, not when
    // moving between child elements (relatedTarget is still inside this node).
    if (e.currentTarget.contains(e.relatedTarget as Node)) return;
    setDragActive(false);
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const dt = e.dataTransfer?.files;
    if (!dt?.length) return;
    Array.from(dt).forEach(validateAndAddFile);
  };

  if (readOnly) {
    return (
      <Stack
        gap="sm"
        p="md"
        style={{
          borderTop: '1px solid rgba(255, 255, 255, 0.06)',
          background: 'rgba(255, 255, 255, 0.02)',
          flexShrink: 0,
        }}
      >
        <Alert icon={<IconAlertCircle size={16} />} color="gray" variant="light">
          <Group justify="space-between" wrap="nowrap" gap="sm">
            <Text size="sm">
              Archivierter Chat — zum Antworten wiederherstellen.
            </Text>
            {onRestore && (
              <Button size="xs" color="cyberOrange" onClick={() => void onRestore()}>
                Wiederherstellen
              </Button>
            )}
          </Group>
        </Alert>
      </Stack>
    );
  }

  return (
    <Box
      style={{
        display: 'grid',
        gridTemplateRows: 'auto auto auto',
        gap: 'var(--mantine-spacing-sm)',
        paddingTop: 'var(--mantine-spacing-md)',
        paddingLeft: 'var(--mantine-spacing-md)',
        paddingRight: reserveFabAffixSpace
          ? 'calc(var(--mantine-spacing-md) + 4.25rem)'
          : 'var(--mantine-spacing-md)',
        // Safe Area (Home-Indikator)
        paddingBottom: 'max(calc(var(--mantine-spacing-md) + env(safe-area-inset-bottom, 0px)), 12px)',
        // dvh: stabil im mobilen Drawer; 55% v. Eltern + hidden hat Eingabe/Icons weggeschnitten
        maxHeight: 'min(62dvh, 100%)',
        minHeight: 0,
        overflowX: 'hidden',
        overflowY: 'auto',
        position: 'relative',
        zIndex: 1,
        borderTop: '1px solid rgba(255, 255, 255, 0.06)',
        /* Eltern-Wrapper in AssistantChat liefert Basis-Fläche; hier opak bei Drag */
        background: dragActive ? 'rgba(255, 102, 0, 0.12)' : 'var(--mantine-color-body)',
        outline: dragActive
          ? '2px dashed var(--mantine-color-cyberOrange-5)'
          : 'none',
        outlineOffset: dragActive ? -6 : 0,
        transition: 'background 120ms ease, outline-color 120ms ease',
      }}
      onDragOver={onDragOver}
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      {displayError ? (
        <Alert icon={<IconAlertCircle size={16} />} color={error ? 'red' : 'yellow'} variant="light">
          <Text size="sm">{displayError}</Text>
        </Alert>
      ) : (
        <div />
      )}

      {/* File Previews */}
      {files.length > 0 ? (
        <Stack gap="xs" style={{ minHeight: 0, maxHeight: 140, overflowY: 'auto' }}>
          {uploadProgress !== null && uploadProgress !== undefined && (
            <Progress value={uploadProgress} color="cyberOrange" size="sm" animated={uploadProgress < 100} striped={uploadProgress < 100} />
          )}

          {isAnalyzingFiles && (
            <Group gap="xs">
              <Loader size="xs" />
              <Text size="sm" c="dimmed">{attachmentStatusLabel(files.map((f) => f.file))}</Text>
            </Group>
          )}

          {isExtractingText && (
            <Group gap="xs">
              <Loader size="xs" color="cyberOrange" />
              <Text size="sm" c="dimmed">Text wird extrahiert…</Text>
            </Group>
          )}

          <Group
            gap="sm"
            wrap="nowrap"
            style={{ overflowX: 'auto', paddingBottom: 6 }}
          >
            {files.map((filePreview) => (
              <Box
                key={filePreview.file.name + '-' + String(filePreview.file.size)}
                style={{
                  position: 'relative',
                  width: 96,
                  height: 96,
                  flexShrink: 0,
                  borderRadius: 10,
                  overflow: 'visible',
                  border: '1px solid var(--mantine-color-dark-4)',
                  background: 'var(--mantine-color-dark-6)',
                }}
              >
                {filePreview.preview ? (
                  <img
                    src={filePreview.preview}
                    alt={filePreview.file.name}
                    title={filePreview.file.name}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      borderRadius: 10,
                      display: 'block',
                    }}
                  />
                ) : (
                  <Box
                    title={filePreview.file.name}
                    style={{
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 4,
                      borderRadius: 10,
                      backgroundColor: 'var(--mantine-color-dark-5)',
                      padding: 6,
                    }}
                  >
                    <IconFile size={32} />
                    <Text size="10px" c="dimmed" lineClamp={1} style={{ maxWidth: '100%' }}>
                      {filePreview.file.name}
                    </Text>
                  </Box>
                )}
                <Tooltip label="Entfernen" withArrow>
                  <ActionIcon
                    size="xs"
                    color="red"
                    radius="xl"
                    variant="filled"
                    onClick={() => removeFile(filePreview.file)}
                    aria-label="Anhang entfernen"
                    style={{
                      position: 'absolute',
                      top: -6,
                      right: -6,
                      boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                    }}
                  >
                    <IconX size={12} />
                  </ActionIcon>
                </Tooltip>
              </Box>
            ))}
          </Group>
          <Text size="xs" c="dimmed">
            {files.length} / {MAX_FILES} Dateien angehängt
          </Text>
        </Stack>
      ) : (
        <div />
      )}

      <KianSlashMenu
        opened={slashOpen}
        onChange={setSlashOpen}
        slashFiltered={slashFiltered}
        onSelect={(d) => {
          setMessage(d.template);
          setSlashOpen(false);
          setPendingHints(d.hints);
          onInputChange?.(d.template);
        }}
      >
        <Box style={{ position: 'relative', width: '100%', minWidth: 0 }}>
          <Textarea
            ref={inputRef}
            placeholder="Frage stellen oder / für Befehle… (Shift+Enter für Zeilenumbruch)"
            value={message}
            onChange={(e) => {
              const newValue = e.currentTarget.value;
              setMessage(newValue);
              setPendingHints(undefined);
              onInputChange?.(newValue);
              if (captureError) clearCaptureError();
            }}
            onKeyDown={handleKeyDown}
            disabled={disabled || isLoading || submitting}
            autosize
            minRows={4}
            maxRows={14}
            style={{ width: '100%' }}
            autoFocus
          />

          {/* Bottom-Toolbar: alle Werkzeuge links, Send rechts — eine Zeile, klare UX */}
          <Group
            justify="space-between"
            wrap="nowrap"
            gap="xs"
            mt="xs"
            style={{
              paddingRight: reserveFabAffixSpace
                ? `calc(${KIAN_FAB_MINIMIZE_SIZE_PX}px + var(--mantine-spacing-xs))`
                : 0,
            }}
          >
            <Group gap={6} wrap="nowrap">
              <Tooltip
                label={forceDeep ? 'PowerMode aktiv — nächste Antwort mit tiefstem Modell' : 'PowerMode: nächste Antwort mit tiefstem Modell erzwingen'}
                withArrow
                position="top"
              >
                <ActionIcon
                  variant={forceDeep ? 'filled' : 'subtle'}
                  color={forceDeep ? 'orange' : 'gray'}
                  size="lg"
                  onClick={() => setForceDeep((v) => !v)}
                  disabled={disabled || isLoading || submitting}
                  aria-label="PowerMode"
                >
                  <IconBolt size={18} />
                </ActionIcon>
              </Tooltip>

              <Tooltip
                label={
                  screenshotSupported
                    ? isCapturing
                      ? 'Screenshot wird erfasst...'
                      : 'Screenshot aufnehmen'
                    : 'Browser unterstützt Screen Capture nicht'
                }
                position="top"
              >
                <ActionIcon
                  variant="subtle"
                  size="lg"
                  onClick={handleCaptureScreenshot}
                  disabled={
                    disabled ||
                    isLoading ||
                    submitting ||
                    isCapturing ||
                    !screenshotSupported ||
                    files.length >= MAX_FILES
                  }
                  loading={isCapturing}
                  aria-label="Screenshot aufnehmen"
                >
                  <IconScreenShare size={18} />
                </ActionIcon>
              </Tooltip>

              <Tooltip label="Datei anhängen (JPG, PNG, GIF, WebP, HEIC, PDF)" position="top">
                <ActionIcon
                  variant="subtle"
                  size="lg"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={disabled || isLoading || submitting || files.length >= MAX_FILES}
                  aria-label="Datei anhängen"
                >
                  <IconPaperclip size={18} />
                </ActionIcon>
              </Tooltip>

              <Tooltip
                label={
                  !voiceSupported
                    ? 'Spracheingabe wird von diesem Browser nicht unterstützt'
                    : voiceStatus === 'listening'
                    ? 'Fertig gesprochen'
                    : voiceStatus === 'transcribing'
                    ? 'Wird in Text umgewandelt …'
                    : serverVoice
                    ? 'Diktieren — sprechen, nach einer kurzen Pause steht der Text im Feld'
                    : 'Spracheingabe starten'
                }
                position="top"
              >
                <ActionIcon
                  variant={voiceStatus === 'listening' ? 'filled' : 'subtle'}
                  color={voiceStatus === 'listening' ? 'red' : undefined}
                  size="lg"
                  onClick={voiceStatus === 'listening' ? stopListening : startListening}
                  loading={voiceStatus === 'transcribing'}
                  disabled={disabled || isLoading || submitting || !voiceSupported || voiceConversationActive}
                  aria-label="Spracheingabe"
                >
                  {voiceStatus === 'listening'
                    ? <IconMicrophoneOff size={18} />
                    : <IconMicrophone size={18} />}
                </ActionIcon>
              </Tooltip>

              {onStartVoiceConversation && serverVoice ? (
                <Tooltip label="Gespräch mit KIAN — sprechen und zuhören" position="top">
                  <ActionIcon
                    variant={voiceConversationActive ? 'filled' : 'subtle'}
                    color={voiceConversationActive ? 'cyberOrange' : undefined}
                    size="lg"
                    onClick={onStartVoiceConversation}
                    disabled={disabled || voiceConversationActive || voiceStatus === 'listening' || voiceStatus === 'transcribing'}
                    aria-label="Gespräch mit KIAN"
                  >
                    <IconHeadset size={18} />
                  </ActionIcon>
                </Tooltip>
              ) : null}
            </Group>

            {isLoading && onAbortStream ? (
              <Tooltip label="Antwort stoppen" withArrow position="top">
                <ActionIcon
                  onClick={() => onAbortStream()}
                  size={reserveFabAffixSpace ? KIAN_FAB_MINIMIZE_SIZE_PX : 44}
                  variant="filled"
                  color="cyberOrange"
                  aria-label="Stoppen"
                >
                  <IconPlayerStop size={20} />
                </ActionIcon>
              </Tooltip>
            ) : (
              <Tooltip label="Absenden (Enter · ⌘/Strg+Enter)" withArrow position="top">
                <ActionIcon
                  onClick={handleSubmit}
                  disabled={(!message.trim() && files.length === 0) || disabled || isLoading || submitting}
                  loading={submitting}
                  size={reserveFabAffixSpace ? KIAN_FAB_MINIMIZE_SIZE_PX : 44}
                  variant="filled"
                  color="cyberOrange"
                  aria-label="Absenden"
                >
                  {submitting ? <IconLoader size={20} /> : <IconSend size={20} />}
                </ActionIcon>
              </Tooltip>
            )}
          </Group>

          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={[...SUPPORTED_TYPES, ...SUPPORTED_EXTENSIONS, ...DATA_EXTENSIONS].join(',')}
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
        </Box>
      </KianSlashMenu>
    </Box>
  );
}
